import { useEffect, useState } from "react";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Col, Input, Row, Select } from "antd";
import { HardwareColumns } from "./Columns";
import ConfirmModal from "../../../global/components/ConfirmModal";
import useDelete from "../../../global/hooks/useDelete";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath } from "react-icons/hi2";
import useGet from "../../../global/hooks/useGet";

const initialFilterState = {
  HardwareNo: "",
  OemNo: "",
  HardwareName: "",
  Product: null,
};

export const Hardware = () => {
  const [hoveredRow, setHoveredRow] = useState(null);
  const [listData, setListData] = useState([]);
  const [params, setParams] = useSearchParams({
    limit: 10,
    sort_by: "HardwareNo",
    order: "desc",
  });
  const logData = JSON.parse(sessionStorage.getItem("userData"));
  const modParams = { ...Object.fromEntries([...params?.entries()]) } || {}; //
  const initialPagination = {
    current: parseInt(modParams.page) || 1,
    pageSize: parseInt(modParams.limit) || 10,
    total: 0,
  }; //
  const [pagination, setPagination] = useState(initialPagination);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [filter, setFilter] = useState(initialFilterState);
  const [filterNoValueText, setFilterNoValueText] = useState("");
  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();

  // to get table data from API server
  const query = new URLSearchParams(params);
  // Remove unwanted keys
  query.delete("pageSize");
  query.delete("total");
  query.delete("current");
  const updatedQuery = query.toString();
  const url = updatedQuery
    ? `${process.env.REACT_APP_API_URL}/api/hardwares/?${updatedQuery}`
    : `${process.env.REACT_APP_API_URL}/api/hardwares/`;
  const { data, loading, error, refetch } = useGet(url);
  const optionUrl = `${process.env.REACT_APP_API_URL}/api/devices/options/`;

  const {
    data: optionData,
    loading: optionDataLoading,
    error: optionDataError,
  } = useGet(optionUrl);
  const modifiedOems = getModifiedResponse(optionData, "oems");
  const modifiedProducts = getModifiedResponse(optionData, "products");

  let deleteUrl;
  selectedRowKeys.length > 1
    ? (deleteUrl = `${process.env.REACT_APP_API_URL}/api/hardwares/bulk-delete/`)
    : (deleteUrl = `${process.env.REACT_APP_API_URL}/api/hardwares/${selectedRowKeys}/`);

  const {
    data: deletedData,
    loading: deletedDataLoading,
    error: deletedDataError,
    deleteData,
    deleteMultiple,
  } = useDelete(deleteUrl);

  useEffect(() => {
    const matchedFilter = {};
    for (const key in initialFilterState) {
      if (modParams[key] !== undefined) {
        matchedFilter[key] = modParams[key];
      }
    }

    setFilter((prev) => ({
      ...prev,
      ...matchedFilter,
    }));
  }, []); //

  const paginationConfig = {
    current: pagination.current, //current page
    pageSize: pagination.pageSize, // items per page
    total: data?.count, // total items
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
      });

      const updatedParams = {
        ...Object.fromEntries([...params.entries()]),
        limit: pageSize,
        page: page,
      };
      setParams(updatedParams);
    },
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
    // navigate(`/oemlist/${newSelectedRowKeys}`);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    fixed: true,
  };

  const onRow = (record, rowIndex) => {
    return {
      onClick: (event) => {
        navigate(`/hardware/info/${record?.HardwareNo}`);
      },
    };
  };

  const handleFilterChange = (e) => {
    const inputName = e.target.name;
    const inputValue = e.target.value.replace(/['""`''""]/g, "");
    setFilter({ ...filter, [inputName]: inputValue });
  };

  const handleFilter = (e) => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        filterDict[key] = filter[key];
      }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";

    if (isEmpty) {
      setFilterNoValueText("No value exists!");
      handleReset();
    } else {
      const params = {
        limit: 10,
        sort_by: "HardwareNo",
        order: "desc",
        page: 1,
        ...filterDict,
      };
      setPagination((prev) => ({ ...prev, current: 1 }));
      setParams(params);
      refetch();
    }
  };

  // reset
  const handleReset = (e) => {
    // Reset the filter state to the initial state
    setFilter({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    }); //
    // Define the new parameters for the API call
    const params = {
      limit: 10,
      sort_by: "HardwareNo", // Make sure to specify sorting if necessary
      order: "desc",
    };
    setParams(params);
    // setSelectedRowKeys([]);
    refetch();
  };

  const onDeleteConfirm = async () => {
    try {
      if (selectedRowKeys.length === 1) {
        await deleteData();
      } else {
        await deleteMultiple({ ids: selectedRowKeys });
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");

      refetch();
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong",
      );
    }
  }, [deletedData, deletedDataError]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data?.data?.map((el) => ({
        ...el,
        key: el?.HardwareNo,
      }));
      setListData(newData);
    }
    // setSelectedRowKeys([]);
  }, [data, loading]);

  const breadcrumbItems = [
    {
      label: "Hardware",
      href: "/hardware",
    },
  ];

  return (
    <div>
      <div className="pb-4 -mt-4 pt-[6px]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems} />
      </div>
      <div className="p-4 bg-white rounded-[4px] border border-gray-200">
        {/* Filter part */}
        <div className="mb-4">
          <div className="mt-2">
            <Row gutter={[16, 16]}>
              {/* <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                <Input
                  className="input_text_3 mr10 !h-full"
                  name="HardwareNo"
                  placeholder="No"
                  value={filter?.HardwareNo}
                  onChange={(e) => {
                    const { value } = e.target;
                    // Allow only positive integers or empty string
                    if (/^[1-9]\d*$/.test(value) || value === "") {
                      handleFilterChange(e);
                    }
                  }}
                />
              </Col> */}
              <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                <Input
                  className="input_text_3 mr10 h-full"
                  placeholder="Name"
                  value={filter.HardwareName}
                  onChange={handleFilterChange}
                  name="HardwareName"
                />
              </Col>
              {![4, 5, 6].includes(logData?.role_value) && (
                <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                  <Select
                    showSearch
                    placeholder="OEM"
                    optionFilterProp="label"
                    value={
                      filter.OemNo || typeof filter.OemNo === "number"
                        ? Number(filter.OemNo)
                        : null
                    }
                    onChange={(value) => setFilter({ ...filter, OemNo: value })}
                    // onSearch={onSearch}
                    options={modifiedOems}
                    style={{ width: "100%" }}
                  />
                </Col>
              )}
              <Col xs={24} sm={24} md={12} lg={8} xl={6}>
                <Select
                  showSearch
                  placeholder="Product"
                  optionFilterProp="label"
                  value={
                    filter.Product || typeof filter.Product === "number"
                      ? Number(filter.Product)
                      : null
                  }
                  onChange={(value) => setFilter({ ...filter, Product: value })}
                  // onSearch={onSearch}
                  options={modifiedProducts}
                  style={{ width: "100%" }}
                />
              </Col>
            </Row>
          </div>

          <div className="w-full overflow-x-auto">
            <div className="flex justify-between my-4 flex-nowrap min-w-[610px]">
              <div className="flex items-center">
                <Button
                  type="primary"
                  className="mr-2 w-[104px] h-[30px] font-medium"
                  onClick={handleFilter}
                >
                  <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
                  Apply
                </Button>
                <Button className="reset-button" onClick={handleReset}>
                  <HiArrowPath className="!text-[14px] font-medium" />
                  Reset
                </Button>
                <Button
                  type="primary"
                  className="mr-2 -ml-2 w-[104px] h-[30px] font-medium"
                  onClick={() => navigate("/hardware/add")}
                >
                  <PlusOutlined />
                  Add
                </Button>
                <Button
                  type="primary"
                  disabled={selectedRowKeys?.length === 0 ? true : false}
                  className="mr-2 w-[104px] h-[30px] font-medium"
                  onClick={() => {
                    setShowModal(true);
                  }}
                >
                  <DeleteOutlined />
                  Delete
                </Button>
              </div>
            </div>
          </div>
          <ConfirmModal
            showModal={showModal}
            setShowModal={setShowModal}
            onDeleteConfirm={onDeleteConfirm}
            setSelectedRowKeys={setSelectedRowKeys}
          />
          <GlobalTable
            columns={HardwareColumns(
              hoveredRow,
              setShowModal,
              setSelectedRowKeys,
              setParams,
              params,
              logData?.role_value === 4 ||
              logData?.role_value === 5 ||
              logData?.role_value === 6,
            )}
            dataSource={listData}
            rowSelection={rowSelection}
            pagination={paginationConfig}
            // onRow={onRow}
            onRow={(record, rowIndex) => {
              return {
                onMouseEnter: () => setHoveredRow(rowIndex),
                onMouseLeave: () => setHoveredRow(null),
                ...onRow?.(record, rowIndex),
              };
            }}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};
